<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import AiBotSettingsAPI from 'dashboard/api/aiBotSettings';

const router = useRouter();

const companies = ref([]);

const dashboardSummary = ref({
  companies: 0,
  ai_active: 0,
  ai_attention: 0,
  ai_requests: 0,
  local_responses: 0,
  total_automated: 0,
  local_percentage: 0,
  ai_cost_brl: 0,
  estimated_savings_brl: 0,
});

const dashboardAttention = ref([]);
const dashboardLoading = ref(false);


const loading = ref(false);
const refreshingOperational = ref(false);
const creating = ref(false);
const error = ref('');
const success = ref('');
const showForm = ref(false);

const searchQuery = ref('');
const statusFilter = ref('all');
const healthFilter = ref('all');
const sortBy = ref('name');


const form = ref({
  name: '',
  responsible: '',
  email: '',
  phone: '',
});

const companyCount = computed(() => companies.value.length);

const latestOperationalCheck = computed(() => {
  const timestamps = companies.value
    .map(company => company.operational_checked_at)
    .filter(Boolean)
    .map(value => new Date(value))
    .filter(date => !Number.isNaN(date.getTime()))
    .map(date => date.getTime());

  if (!timestamps.length) {
    return null;
  }

  return new Date(Math.max(...timestamps));
});

const latestOperationalCheckLabel = computed(() => {
  const date = latestOperationalCheck.value;

  if (!date) {
    return 'Ainda não verificado';
  }

  return date.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
});

const operationalMonitorStatus = computed(() => {
  const date = latestOperationalCheck.value;

  if (!date) {
    return {
      level: 'never',
      label: 'Nunca executado',
      description: 'Nenhuma verificação registrada',
    };
  }

  const ageMinutes = Math.max(
    0,
    Math.floor(
      (Date.now() - date.getTime()) / 60000
    )
  );

  if (ageMinutes <= 10) {
    return {
      level: 'healthy',
      label: 'Atualizado',
      description:
        ageMinutes === 0
          ? 'Verificado agora'
          : `Verificado há ${ageMinutes} min`,
    };
  }

  if (ageMinutes <= 20) {
    return {
      level: 'warning',
      label: 'Atrasado',
      description: `Sem atualização há ${ageMinutes} min`,
    };
  }

  return {
    level: 'critical',
    label: 'Monitor sem atualização',
    description: `Sem atualização há ${ageMinutes} min`,
  };
});

const operationalMonitorClass = computed(() => {
  const level =
    operationalMonitorStatus.value.level;

  if (level === 'healthy') {
    return (
      'border-n-teal-6 bg-n-teal-2 ' +
      'text-n-teal-11'
    );
  }

  if (level === 'warning') {
    return (
      'border-n-amber-6 bg-n-amber-2 ' +
      'text-n-amber-11'
    );
  }

  if (level === 'critical') {
    return (
      'border-n-ruby-6 bg-n-ruby-2 ' +
      'text-n-ruby-11'
    );
  }

  return (
    'border-n-weak bg-n-alpha-2 ' +
    'text-n-slate-11'
  );
});

const operationalMonitorDotClass = computed(() => {
  const level =
    operationalMonitorStatus.value.level;

  if (level === 'healthy') {
    return 'bg-n-teal-9';
  }

  if (level === 'warning') {
    return 'bg-n-amber-9';
  }

  if (level === 'critical') {
    return 'bg-n-ruby-9';
  }

  return 'bg-n-slate-8';
});


const setHealthFilter = status => {
  healthFilter.value =
    healthFilter.value === status
      ? 'all'
      : status;
};

const inactivityDays = company => {
  if (!company.last_activity_at) {
    return null;
  }

  const last = new Date(
    company.last_activity_at
  );

  if (Number.isNaN(last.getTime())) {
    return null;
  }

  const diff =
    Date.now() - last.getTime();

  if (diff <= 0) {
    return 0;
  }

  return Math.floor(
    diff / 86400000
  );
};

const inactivityLabel = company => {
  if (!company.last_activity_at) {
    return 'Nunca teve atividade';
  }

  const days = inactivityDays(company);

  if (days === null) {
    return 'Atividade desconhecida';
  }

  if (days === 0) {
    return 'Atividade hoje';
  }

  if (days === 1) {
    return 'Sem atividade há 1 dia';
  }

  return `Sem atividade há ${days} dias`;
};

const isInactiveForDays = (
  company,
  minimumDays
) => {
  if (!company.last_activity_at) {
    return true;
  }

  const days = inactivityDays(company);

  if (days === null) {
    return true;
  }

  return days >= minimumDays;
};

const formatLastActivity = value => {
  if (!value) {
    return 'Sem atividade';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Sem atividade';
  }

  return date.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const lastActivityTypeLabel = company => {
  if (
    company.last_activity_type === 'local'
  ) {
    return 'Resposta local';
  }

  if (
    company.last_activity_type === 'ai'
  ) {
    return 'Inteligência Artificial';
  }

  return 'Nenhuma atividade registrada';
};

const companyRecommendedActions = company => {
  const actions = [];

  const limit = Number(
    company.monthly_cost_limit_brl || 0
  );

  const limitPercent = Number(
    company.monthly_limit_percent || 0
  );

  const days = inactivityDays(company);

  const evolutionOffline =
    company.whatsapp_provider === 'evolution' &&
    Boolean(company.evolution_instance_name) &&
    company.evolution_state !== 'open';

  if (evolutionOffline) {
    actions.push({
      key: 'whatsapp_offline',
      priority: 1,
      level: 'critical',
      title: 'Reconectar WhatsApp',
      description:
        'A instância Evolution está desconectada.',
    });
  }

  if (
    limit > 0 &&
    limitPercent >= 100
  ) {
    actions.push({
      key: 'limit_reached',
      priority: 2,
      level: 'critical',
      title: 'Revisar limite mensal',
      description:
        'O consumo da IA atingiu o limite configurado.',
    });
  } else if (
    limit > 0 &&
    limitPercent >= 80
  ) {
    actions.push({
      key: 'near_limit',
      priority: 3,
      level: 'warning',
      title: 'Acompanhar consumo da IA',
      description:
        `Consumo em ${limitPercent.toFixed(0)}% do limite mensal.`,
    });
  }

  if (!company.last_activity_at) {
    actions.push({
      key: 'never_active',
      priority: 4,
      level: 'critical',
      title: 'Verificar empresa sem atividade',
      description:
        'Nenhuma atividade de automação foi registrada.',
    });
  } else if (
    days !== null &&
    days >= 30
  ) {
    actions.push({
      key: 'inactive_30d',
      priority: 4,
      level: 'critical',
      title: 'Verificar empresa sem atividade',
      description:
        `Sem atividade de automação há ${days} dias.`,
    });
  } else if (
    days !== null &&
    days >= 7
  ) {
    actions.push({
      key: 'inactive_7d',
      priority: 5,
      level: 'warning',
      title: 'Verificar baixa atividade',
      description:
        `Sem atividade de automação há ${days} dias.`,
    });
  }

  if (company.ai_status === 'disabled') {
    actions.push({
      key: 'ai_disabled',
      priority: 6,
      level: 'info',
      title: 'Verificar configuração da IA',
      description:
        'A Inteligência Artificial está desativada.',
    });
  }

  return actions.sort(
    (a, b) => a.priority - b.priority
  );
};

const companyNeedsAction = company =>
  companyRecommendedActions(company).length > 0;

const companiesNeedingActionCount = computed(
  () =>
    companies.value.filter(
      company =>
        companyNeedsAction(company)
    ).length
);

const recommendedActionLevelClass = level => {
  switch (level) {
    case 'critical':
      return 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11';

    case 'warning':
      return 'border-n-amber-6 bg-n-amber-3 text-n-amber-11';

    case 'info':
      return 'border-n-blue-6 bg-n-blue-3 text-n-blue-11';

    default:
      return 'border-n-weak bg-n-alpha-1 text-n-slate-11';
  }
};

const filteredCompanies = computed(() => {
  const search =
    searchQuery.value.trim().toLowerCase();

  let result = [...companies.value];

  if (search) {
    result = result.filter(company => {
      const fields = [
        company.name,
        company.responsible,
        company.email,
        company.phone,
        company.evolution_instance_name,
      ];

      return fields.some(value =>
        String(value || '')
          .toLowerCase()
          .includes(search)
      );
    });
  }

  switch (statusFilter.value) {
    case 'active':
      result = result.filter(
        company =>
          company.ai_status === 'active' ||
          company.ai_status === 'limit_alert'
      );
      break;

    case 'paused':
      result = result.filter(
        company =>
          company.ai_status === 'paused_limit'
      );
      break;

    case 'disabled':
      result = result.filter(
        company =>
          company.ai_status === 'disabled'
      );
      break;

    case 'whatsapp_offline':
      result = result.filter(
        company =>
          company.whatsapp_provider ===
            'evolution' &&
          Boolean(
            company.evolution_instance_name
          ) &&
          company.evolution_state !==
            'open'
      );
      break;

    case 'inactive_3d':

      result = result.filter(

        company =>

          isInactiveForDays(company, 3)

      );

      break;


    case 'inactive_7d':

      result = result.filter(

        company =>

          isInactiveForDays(company, 7)

      );

      break;


    case 'inactive_30d':

      result = result.filter(

        company =>

          isInactiveForDays(company, 30)

      );

      break;


    case 'near_limit':
      result = result.filter(company => {
        const limit = Number(
          company.monthly_cost_limit_brl || 0
        );

        const percent = Number(
          company.monthly_limit_percent || 0
        );

        return limit > 0 && percent >= 80;
      });
      break;

    default:
      break;
  }

  result.sort((a, b) => {
    switch (sortBy.value) {
      case 'activity_desc': {
        const aTime = a.last_activity_at
          ? new Date(a.last_activity_at).getTime()
          : 0;

        const bTime = b.last_activity_at
          ? new Date(b.last_activity_at).getTime()
          : 0;

        return bTime - aTime;
      }

      case 'activity_none': {
        const aEmpty = !a.last_activity_at;
        const bEmpty = !b.last_activity_at;

        if (aEmpty !== bEmpty) {
          return aEmpty ? -1 : 1;
        }

        const aTime = a.last_activity_at
          ? new Date(a.last_activity_at).getTime()
          : 0;

        const bTime = b.last_activity_at
          ? new Date(b.last_activity_at).getTime()
          : 0;

        return aTime - bTime;
      }

      case 'cost_desc':
        return (
          Number(b.monthly_cost_brl || 0) -
          Number(a.monthly_cost_brl || 0)
        );

      case 'usage_desc':
        return (
          Number(
            b.monthly_limit_percent || 0
          ) -
          Number(
            a.monthly_limit_percent || 0
          )
        );

      case 'name_desc':
        return String(b.name || '')
          .localeCompare(
            String(a.name || ''),
            'pt-BR'
          );

      case 'name':
      default:
        return String(a.name || '')
          .localeCompare(
            String(b.name || ''),
            'pt-BR'
          );
    }
  });

  if (
    healthFilter.value !== 'all'
  ) {
    result = result.filter(
      company =>
        companyHealth(company).status ===
        healthFilter.value
    );
  }

  return result;
});

const filteredCompanyCount = computed(
  () => filteredCompanies.value.length
);

const activeAiCount = computed(
  () =>
    companies.value.filter(
      company =>
        company.ai_status === 'active' ||
        company.ai_status === 'limit_alert'
    ).length
);

const pausedAiCount = computed(
  () =>
    companies.value.filter(
      company => company.ai_status === 'paused_limit'
    ).length
);

const disabledAiCount = computed(
  () =>
    companies.value.filter(
      company => company.ai_status === 'disabled'
    ).length
);

const totalMonthlyCost = computed(() =>
  companies.value.reduce(
    (total, company) =>
      total + Number(company.monthly_cost_brl || 0),
    0
  )
);

const totalMonthlyRequests = computed(() =>
  companies.value.reduce(
    (total, company) =>
      total + Number(company.monthly_requests || 0),
    0
  )
);

const totalMonthlyLocalResponses = computed(() =>
  companies.value.reduce(
    (total, company) =>
      total +
      Number(company.monthly_local_responses || 0),
    0
  )
);

const totalMonthlyAutomations = computed(() =>
  totalMonthlyRequests.value +
  totalMonthlyLocalResponses.value
);

const totalLocalPercentage = computed(() => {
  if (!totalMonthlyAutomations.value) {
    return 0;
  }

  return (
    totalMonthlyLocalResponses.value /
    totalMonthlyAutomations.value *
    100
  );
});

const formatBRL = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatNumber = value => {
  return Number(value || 0).toLocaleString('pt-BR');
};

const aiStatusLabel = company => {
  switch (company.ai_status) {
    case 'paused_limit':
      return 'IA pausada por limite';

    case 'limit_alert':
      return 'IA ativa — limite atingido';

    case 'disabled':
      return 'IA desativada';

    default:
      return 'IA ativa';
  }
};

const aiStatusClass = company => {
  switch (company.ai_status) {
    case 'paused_limit':
      return 'text-n-ruby-11';

    case 'limit_alert':
      return 'text-n-amber-11';

    case 'disabled':
      return 'text-n-slate-10';

    default:
      return 'text-n-teal-11';
  }
};

const aiStatusDotClass = company => {
  switch (company.ai_status) {
    case 'paused_limit':
      return 'bg-n-ruby-9';

    case 'limit_alert':
      return 'bg-n-amber-9';

    case 'disabled':
      return 'bg-n-slate-7';

    default:
      return 'bg-n-teal-9';
  }
};

const modelLabel = model => {
  if (model === 'gpt-5.6-luna') {
    return 'GPT-5.6 Luna';
  }

  if (model === 'gpt-5.6') {
    return 'GPT-5.6';
  }

  return model || 'Não definido';
};

const usagePercent = company => {
  const limit = Number(
    company.monthly_cost_limit_brl || 0
  );

  const cost = Number(
    company.monthly_cost_brl || 0
  );

  if (limit <= 0) {
    return 0;
  }

  return Math.max(
    0,
    Math.min(100, (cost / limit) * 100)
  );
};

const usagePercentLabel = company => {
  const value = Number(
    company.monthly_limit_percent || 0
  );

  return `${value.toFixed(1)}%`;
};

const usageBarClass = company => {
  if (company.ai_status === 'paused_limit') {
    return 'bg-n-ruby-9';
  }

  if (
    Number(company.monthly_limit_percent || 0) >= 80
  ) {
    return 'bg-n-amber-9';
  }

  return 'bg-n-teal-9';
};



const formatDashboardCurrency = value =>
  Number(value || 0).toLocaleString(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const formatDashboardPercent = value =>
  Number(value || 0).toLocaleString(
    'pt-BR',
    {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }
  );


const companyWhatsappOffline = company => {
  if (
    company.whatsapp_provider !== 'evolution'
  ) {
    return false;
  }

  if (
    !company.evolution_instance_name
  ) {
    return false;
  }

  return (
    company.evolution_state !== 'open'
  );
};


const companyHealth = company => {
  const whatsappOffline =
    companyWhatsappOffline(company);

  const limit =
    Number(
      company.monthly_cost_limit_brl || 0
    );

  const percent =
    Number(
      company.monthly_limit_percent || 0
    );

  const aiLimitLevel =
    company.ai_limit_level || 'normal';

  const inactivityLevel =
    company.inactivity_level || 'never_used';

  // 1. Problemas críticos têm prioridade máxima.
  if (whatsappOffline) {
    return {
      status: 'critical',
      label: 'Crítico',
      description: 'WhatsApp desconectado',
    };
  }

  if (
    aiLimitLevel === 'critical' ||
    (
      limit > 0 &&
      percent >= 100
    )
  ) {
    return {
      status: 'critical',
      label: 'Crítico',
      description: 'Limite da IA atingido',
    };
  }

  if (inactivityLevel === 'critical') {
    return {
      status: 'critical',
      label: 'Crítico',
      description: '30+ dias sem atividade',
    };
  }

  // 2. Alertas operacionais.
  if (
    aiLimitLevel === 'warning' ||
    (
      limit > 0 &&
      percent >= 80
    )
  ) {
    return {
      status: 'warning',
      label: 'Atenção',
      description: 'Consumo elevado da IA',
    };
  }

  if (inactivityLevel === 'warning') {
    return {
      status: 'warning',
      label: 'Atenção',
      description: '7+ dias sem atividade',
    };
  }

  // 3. IA explicitamente desativada.
  if (company.ai_status === 'disabled') {
    return {
      status: 'inactive',
      label: 'Inativo',
      description: 'IA desativada',
    };
  }

  // 4. Empresa configurada, mas ainda sem uso da automação.
  if (inactivityLevel === 'never_used') {
    return {
      status: 'never_used',
      label: 'Nunca utilizado',
      description: 'Automação ainda não utilizada',
    };
  }

  // 5. Nenhum problema encontrado.
  return {
    status: 'healthy',
    label: 'Saudável',
    description: 'Operação normal',
  };
};

const companyHealthClass = company => {
  const status =
    companyHealth(company).status;

  if (status === 'critical') {
    return (
      'bg-n-ruby-3 text-n-ruby-11 ' +
      'border-n-ruby-6'
    );
  }

  if (status === 'warning') {
    return (
      'bg-n-amber-3 text-n-amber-11 ' +
      'border-n-amber-6'
    );
  }

  if (status === 'inactive') {
    return (
      'bg-n-slate-3 text-n-slate-11 ' +
      'border-n-slate-6'
    );
  }

  if (status === 'never_used') {
    return (
      'bg-n-blue-3 text-n-blue-11 ' +
      'border-n-blue-6'
    );
  }

  return (
    'bg-n-teal-3 text-n-teal-11 ' +
    'border-n-teal-6'
  );
};

const companyHealthDotClass = company => {
  const status =
    companyHealth(company).status;

  if (status === 'critical') {
    return 'bg-n-ruby-9';
  }

  if (status === 'warning') {
    return 'bg-n-amber-9';
  }

  if (status === 'inactive') {
    return 'bg-n-slate-8';
  }

  if (status === 'never_used') {
    return 'bg-n-blue-9';
  }

  return 'bg-n-teal-9';
};

const companyAttentionReasons = company => {
  const reasons = [];

  if (companyWhatsappOffline(company)) {
    reasons.push({
      type: 'whatsapp',
      priority: 1,
      label: 'WhatsApp desconectado',
    });
  }

  const limit =
    Number(
      company.monthly_cost_limit_brl || 0
    );

  const percent =
    Number(
      company.monthly_limit_percent || 0
    );

  if (
    limit > 0 &&
    percent >= 100
  ) {
    reasons.push({
      type: 'limit',
      priority: 2,
      label: 'Limite da IA atingido',
    });
  } else if (
    limit > 0 &&
    percent >= 80
  ) {
    reasons.push({
      type: 'near_limit',
      priority: 3,
      label: 'Consumo da IA acima de 80%',
    });
  }

  if (
    company.ai_status === 'disabled'
  ) {
    reasons.push({
      type: 'disabled',
      priority: 4,
      label: 'IA desativada',
    });
  }

  return reasons.sort(
    (a, b) => a.priority - b.priority
  );
};

const companyAttentionItems = computed(() => {
  return companies.value
    .map(company => {
      const reasons =
        companyAttentionReasons(company);

      return {
        ...company,
        attention_reasons: reasons,
        attention_priority:
          reasons.length
            ? reasons[0].priority
            : 999,
      };
    })
    .filter(
      company =>
        company.attention_reasons.length > 0
    )
    .sort((a, b) => {
      if (
        a.attention_priority !==
        b.attention_priority
      ) {
        return (
          a.attention_priority -
          b.attention_priority
        );
      }

      const usageDiff =
        Number(
          b.monthly_limit_percent || 0
        ) -
        Number(
          a.monthly_limit_percent || 0
        );

      if (usageDiff !== 0) {
        return usageDiff;
      }

      return String(a.name || '')
        .localeCompare(
          String(b.name || ''),
          'pt-BR'
        );
    });
});



const healthCounters = computed(() => {
  const counters = {
    healthy: 0,
    warning: 0,
    critical: 0,
    inactive: 0,
    never_used: 0,
  };

  for (
    const company of companies.value
  ) {
    const status =
      companyHealth(company).status;

    if (
      Object.prototype.hasOwnProperty.call(
        counters,
        status
      )
    ) {
      counters[status] += 1;
    }
  }

  return counters;
});

const attentionCounters = computed(() => {
  const counters = {
    whatsapp: 0,
    limit: 0,
    near_limit: 0,
    disabled: 0,
  };

  for (
    const company of companyAttentionItems.value
  ) {
    for (
      const reason of company.attention_reasons
    ) {
      if (
        Object.prototype.hasOwnProperty.call(
          counters,
          reason.type
        )
      ) {
        counters[reason.type] += 1;
      }
    }
  }

  return counters;
});



const dashboardStatusLabel = status => {
  if (status === 'critical') {
    return 'Limite atingido';
  }

  if (status === 'warning') {
    return 'Próximo do limite';
  }

  if (status === 'disabled') {
    return 'IA desativada';
  }

  return 'Saudável';
};

const loadDashboardSummary = async () => {
  dashboardLoading.value = true;

  try {
    const response =
      await AiBotSettingsAPI
        .getCompaniesDashboardSummary();

    const data =
      response?.data || {};

    dashboardSummary.value = {
      ...dashboardSummary.value,
      ...(data.summary || {}),
    };

    dashboardAttention.value =
      Array.isArray(data.attention)
        ? data.attention
        : [];
  } catch (error) {
    console.error(
      'Erro ao carregar resumo central:',
      error
    );
  } finally {
    dashboardLoading.value = false;
  }
};

const refreshOperationalStates = async () => {
  if (refreshingOperational.value) {
    return;
  }

  refreshingOperational.value = true;
  error.value = '';
  success.value = '';

  try {
    const response =
      await AiBotSettingsAPI
        .refreshOperationalStates();

    await Promise.all([
      loadCompanies(),
      loadDashboardSummary(),
    ]);

    success.value =
      response?.data?.message ||
      'Estados operacionais atualizados.';
  } catch (err) {
    console.error(
      'Erro ao atualizar estados operacionais:',
      err
    );

    error.value =
      err.response?.data?.error ||
      'Não foi possível atualizar os estados operacionais.';
  } finally {
    refreshingOperational.value = false;
  }
};


const loadCompanies = async () => {
  loading.value = true;
  error.value = '';

  try {
    const response = await AiBotSettingsAPI.getCompanies();
    companies.value = response.data?.companies || [];
  } catch (err) {
    console.error('Erro ao carregar empresas:', err);
    error.value = 'Não foi possível carregar as empresas.';
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  form.value = {
    name: '',
    responsible: '',
    email: '',
    phone: '',
    };
};

const createCompany = async () => {
  if (!form.value.name.trim()) {
    error.value = 'Informe o nome da empresa.';
    return;
  }

  creating.value = true;
  error.value = '';
  success.value = '';

  try {
    const response = await AiBotSettingsAPI.createCompany({
      name: form.value.name.trim(),
      responsible: form.value.responsible.trim(),
      email: form.value.email.trim(),
      phone: form.value.phone.trim(),
    });

    const result = response.data || {};

    success.value = 'Empresa criada com sucesso.';
    resetForm();
    showForm.value = false;

    await loadCompanies();

    if (result.redirect_account_id) {
      router.push({
        name: 'ai_bot_settings',
        params: {
          accountId: result.redirect_account_id,
        },
      });
    }
  } catch (err) {
    console.error('Erro ao criar empresa:', err);

    error.value =
      err.response?.data?.error ||
      err.response?.data?.message ||
      'Não foi possível criar a empresa.';
  } finally {
    creating.value = false;
  }
};

const toggleCompany = async company => {
  error.value = '';
  success.value = '';

  try {
    const response = await AiBotSettingsAPI.toggleCompany(company.id);

    success.value = response.data?.disabled
      ? `Empresa ${company.name} desativada.`
      : `Empresa ${company.name} reativada.`;

    await loadCompanies();
  } catch (err) {
    console.error('Erro ao alterar empresa:', err);

    error.value =
      err.response?.data?.error ||
      'Não foi possível alterar o status da empresa.';
  }
};

const deleteCompany = async company => {
  const confirmation = window.prompt(
    `ATENÇÃO: esta ação é definitiva.\n\nDigite exatamente o nome da empresa para excluir:\n${company.name}`
  );

  if (confirmation === null) {
    return;
  }

  if (confirmation !== company.name) {
    error.value = 'O nome digitado não confere. Exclusão cancelada.';
    return;
  }

  const finalConfirmation = window.confirm(
    `Excluir definitivamente "${company.name}"?\n\nConversas, Inbox, times, configuração da IA e Account serão removidos.`
  );

  if (!finalConfirmation) {
    return;
  }

  error.value = '';
  success.value = '';

  try {
    await AiBotSettingsAPI.deleteCompany(
      company.id,
      confirmation
    );

    success.value =
      `Empresa ${company.name} excluída com sucesso.`;

    await loadCompanies();
  } catch (err) {
    console.error('Erro ao excluir empresa:', err);

    error.value =
      err.response?.data?.error ||
      'Não foi possível excluir a empresa.';
  }
};

const manageCompany = company => {
  router.push({
    name: 'ai_company_manage',
    params: {
      companyId: company.id,
    },
  });
};

const whatsappLabel = company => {
  if (!company.evolution_instance_name) {
    return 'Não configurado';
  }

  if (company.evolution_state === 'open') {
    return 'Conectado';
  }

  if (company.evolution_state === 'close') {
    return 'Desconectado';
  }

  if (company.evolution_state === 'unavailable') {
    return 'Indisponível';
  }

  return company.evolution_state || 'Desconhecido';
};

const whatsappClass = company => {
  if (company.evolution_state === 'open') {
    return 'text-n-teal-11';
  }

  if (
    company.evolution_state === 'close' ||
    company.evolution_state === 'unavailable'
  ) {
    return 'text-n-ruby-11';
  }

  return 'text-n-slate-11';
};

onMounted(() => {
  loadDashboardSummary();
  loadCompanies();
});
</script>

<template>
  <div class="w-full h-full overflow-auto bg-n-background">

    <!-- CENTRAL DE AUTOMAÇÃO -->
    <div
      class="mb-6 space-y-4"
    >
      <div
        class="flex flex-wrap items-start justify-between gap-4"
      >
        <div>
          <h2
            class="text-xl font-semibold text-n-slate-12"
          >
            Central de automação
          </h2>

          <p
            class="mt-1 text-sm text-n-slate-10"
          >
            Visão geral de todas as empresas
            no mês atual.
          </p>
        </div>

        <span
          v-if="
            Number(
              companyAttentionItems.length || 0
            ) > 0
          "
          class="px-3 py-1.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
        >
          {{
            companyAttentionItems.length
          }}
          empresa(s) precisam de atenção
        </span>

        <span
          v-else
          class="px-3 py-1.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
        >
          Tudo saudável
        </span>
      </div>


      
      <!-- SAÚDE DAS EMPRESAS -->
      <div
        class="p-4 border rounded-xl border-n-weak bg-n-background"
      >
        <div>
          <h3
            class="text-sm font-semibold text-n-slate-12"
          >
            Saúde das empresas
          </h3>

          <p
            class="mt-1 text-xs text-n-slate-10"
          >
            Situação operacional atual das empresas.
          </p>

            <div
              class="flex flex-wrap items-center justify-between gap-2 mt-3"
            >
              <p
                class="text-xs text-n-slate-10"
              >
                Exibindo
                <strong
                  class="font-semibold text-n-slate-12"
                >
                  {{
                    Number(
                      filteredCompanyCount || 0
                    ).toLocaleString('pt-BR')
                  }}
                </strong>
                de
                <strong
                  class="font-semibold text-n-slate-12"
                >
                  {{
                    Number(
                      companyCount || 0
                    ).toLocaleString('pt-BR')
                  }}
                </strong>
                empresas
              </p>

              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium transition border rounded-lg"
                :class="
                  healthFilter === 'all'
                    ? 'border-n-brand bg-n-brand text-white'
                    : 'border-n-weak bg-n-background text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="healthFilter = 'all'"
              >
                Todas as empresas
              </button>

              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium transition border rounded-lg border-n-weak bg-n-background text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="refreshingOperational"
                @click="refreshOperationalStates"
              >
                {{
                  refreshingOperational
                    ? 'Atualizando...'
                    : 'Atualizar agora'
                }}
              </button>

              <span
                class="text-xs text-n-slate-10"
              >
                Última verificação:
                <strong
                  class="font-medium text-n-slate-12"
                >
                  {{ latestOperationalCheckLabel }}
                </strong>
              </span>

              <div
                class="flex items-center gap-2 px-3 py-1.5 text-xs font-medium border rounded-lg"
                :class="operationalMonitorClass"
                :title="
                  operationalMonitorStatus.description
                "
              >
                <span
                  class="w-2 h-2 rounded-full"
                  :class="operationalMonitorDotClass"
                />

                <span>
                  {{
                    operationalMonitorStatus.label
                  }}
                </span>

                <span
                  class="font-normal opacity-75"
                >
                  · Automático a cada 5 min
                </span>
              </div>
            </div>

            <div
              v-if="healthFilter !== 'all'"
              class="flex flex-wrap items-center gap-2 mt-2 text-xs text-n-slate-10"
            >
              <span>
                Filtro de saúde ativo:
              </span>

              <strong
                class="text-n-slate-12"
              >
                {{
                  healthFilter === 'healthy'
                    ? 'Saudáveis'
                    : healthFilter === 'warning'
                      ? 'Atenção'
                      : healthFilter === 'critical'
                        ? 'Críticos'
                        : healthFilter === 'never_used'
                          ? 'Nunca utilizados'
                          : 'Inativos'
                }}
              </strong>

              <button
                type="button"
                class="font-medium text-n-brand hover:underline"
                @click="healthFilter = 'all'"
              >
                Limpar
              </button>
            </div>
        </div>

        <div
          class="grid grid-cols-2 gap-2 mt-4 lg:grid-cols-5"
        >
          <div
            class="p-3 border rounded-xl border-n-teal-6 bg-n-teal-2 cursor-pointer transition hover:opacity-80"
          
              :class="{
                'ring-2 ring-n-teal-9':
                  healthFilter === 'healthy',
              }"
              @click="setHealthFilter('healthy')"
>
            <p
              class="text-xs font-medium text-n-teal-11"
            >
              Saudáveis
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-teal-11"
            >
              {{
                Number(
                  healthCounters.healthy || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>

          <div
            class="p-3 border rounded-xl border-n-amber-6 bg-n-amber-2 cursor-pointer transition hover:opacity-80"
          
              :class="{
                'ring-2 ring-n-amber-9':
                  healthFilter === 'warning',
              }"
              @click="setHealthFilter('warning')"
>
            <p
              class="text-xs font-medium text-n-amber-11"
            >
              Atenção
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-amber-11"
            >
              {{
                Number(
                  healthCounters.warning || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>

          <div
            class="p-3 border rounded-xl border-n-ruby-6 bg-n-ruby-2 cursor-pointer transition hover:opacity-80"
          
              :class="{
                'ring-2 ring-n-ruby-9':
                  healthFilter === 'critical',
              }"
              @click="setHealthFilter('critical')"
>
            <p
              class="text-xs font-medium text-n-ruby-11"
            >
              Críticos
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-ruby-11"
            >
              {{
                Number(
                  healthCounters.critical || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>

          <div
            class="p-3 border rounded-xl border-n-weak bg-n-alpha-2 cursor-pointer transition hover:opacity-80"
          
              :class="{
                'ring-2 ring-n-slate-8':
                  healthFilter === 'inactive',
              }"
              @click="setHealthFilter('inactive')"
>
            <p
              class="text-xs font-medium text-n-slate-10"
            >
              Inativos
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-slate-12"
            >
              {{
                Number(
                  healthCounters.inactive || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>

          <div
            class="p-3 border rounded-xl border-n-blue-6 bg-n-blue-2 cursor-pointer transition hover:opacity-80"
            :class="{
              'ring-2 ring-n-blue-9':
                healthFilter === 'never_used',
            }"
            @click="setHealthFilter('never_used')"
          >
            <p
              class="text-xs font-medium text-n-blue-11"
            >
              Nunca utilizados
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-blue-11"
            >
              {{
                Number(
                  healthCounters.never_used || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>
        </div>
      </div>

<!-- INDICADORES -->
      <div
        class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
      >
        <div
          class="p-4 border rounded-xl border-n-weak bg-n-background"
        >
          <p
            class="text-xs font-medium text-n-slate-10"
          >
            Empresas
          </p>

          <p
            class="mt-2 text-2xl font-semibold text-n-slate-12"
          >
            {{
              Number(
                dashboardSummary.companies || 0
              ).toLocaleString('pt-BR')
            }}
          </p>

          <p
            class="mt-1 text-xs text-n-slate-10"
          >
            {{
              Number(
                dashboardSummary.ai_active || 0
              ).toLocaleString('pt-BR')
            }}
            com IA ativa
          </p>
        </div>


        <div
          class="p-4 border rounded-xl border-n-weak bg-n-background"
        >
          <p
            class="text-xs font-medium text-n-slate-10"
          >
            Total automatizado
          </p>

          <p
            class="mt-2 text-2xl font-semibold text-n-slate-12"
          >
            {{
              Number(
                dashboardSummary.total_automated || 0
              ).toLocaleString('pt-BR')
            }}
          </p>

          <p
            class="mt-1 text-xs text-n-slate-10"
          >
            IA + respostas locais
          </p>
        </div>


        <div
          class="p-4 border rounded-xl border-n-weak bg-n-background"
        >
          <p
            class="text-xs font-medium text-n-slate-10"
          >
            Resolvido sem IA
          </p>

          <p
            class="mt-2 text-2xl font-semibold text-n-teal-11"
          >
            {{
              Number(
                dashboardSummary.local_responses || 0
              ).toLocaleString('pt-BR')
            }}
          </p>

          <p
            class="mt-1 text-xs text-n-slate-10"
          >
            {{
              formatDashboardPercent(
                dashboardSummary.local_percentage
              )
            }}%
            das automações
          </p>
        </div>


        <div
          class="p-4 border rounded-xl border-n-weak bg-n-background"
        >
          <p
            class="text-xs font-medium text-n-slate-10"
          >
            Chamadas IA
          </p>

          <p
            class="mt-2 text-2xl font-semibold text-n-slate-12"
          >
            {{
              Number(
                dashboardSummary.ai_requests || 0
              ).toLocaleString('pt-BR')
            }}
          </p>

          <p
            class="mt-1 text-xs text-n-slate-10"
          >
            Requisições pagas
          </p>
        </div>
      </div>


      <!-- FINANCEIRO -->
      <div
        class="grid grid-cols-1 gap-3 md:grid-cols-2"
      >
        <div
          class="p-4 border rounded-xl border-n-weak bg-n-background"
        >
          <p
            class="text-xs font-medium text-n-slate-10"
          >
            Custo total da IA no mês
          </p>

          <p
            class="mt-2 text-xl font-semibold text-n-slate-12"
          >
            {{
              formatDashboardCurrency(
                dashboardSummary.ai_cost_brl
              )
            }}
          </p>
        </div>


        <div
          class="p-4 border rounded-xl border-n-teal-6 bg-n-teal-2"
        >
          <p
            class="text-xs font-medium text-n-teal-11"
          >
            Economia estimada
          </p>

          <p
            class="mt-2 text-xl font-semibold text-n-teal-11"
          >
            {{
              formatDashboardCurrency(
                dashboardSummary
                  .estimated_savings_brl
              )
            }}
          </p>

          <p
            class="mt-1 text-xs text-n-slate-10"
          >
            Chamadas de IA evitadas pelas
            respostas locais
          </p>
        </div>
      </div>


      <!-- ATENÇÃO NECESSÁRIA -->
      <div
        class="p-5 border rounded-xl border-n-weak bg-n-background"
      >
        <div
          class="flex flex-wrap items-start justify-between gap-3"
        >
          <div>
            <h3
              class="text-base font-semibold text-n-slate-12"
            >
              Atenção necessária
            </h3>

            <p
              class="mt-1 text-sm text-n-slate-10"
            >
              WhatsApp desconectado, limite de IA,
              consumo elevado ou IA desativada.
            </p>
          </div>

          <span
            v-if="companyAttentionItems.length"
            class="px-2.5 py-1 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
          >
            {{ companyAttentionItems.length }}
          </span>
        </div>


        
        <!-- Resumo dos alertas operacionais -->
        <div
          class="grid grid-cols-2 gap-2 mt-4 md:grid-cols-4"
        >
          <div
            class="p-3 border rounded-xl border-n-ruby-6 bg-n-ruby-2"
          >
            <p
              class="text-xs font-medium text-n-ruby-11"
            >
              WhatsApp offline
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-ruby-11"
            >
              {{
                Number(
                  attentionCounters.whatsapp || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>


          <div
            class="p-3 border rounded-xl border-n-ruby-6 bg-n-ruby-2"
          >
            <p
              class="text-xs font-medium text-n-ruby-11"
            >
              Limite atingido
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-ruby-11"
            >
              {{
                Number(
                  attentionCounters.limit || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>


          <div
            class="p-3 border rounded-xl border-n-amber-6 bg-n-amber-2"
          >
            <p
              class="text-xs font-medium text-n-amber-11"
            >
              Próximo do limite
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-amber-11"
            >
              {{
                Number(
                  attentionCounters.near_limit || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>


          <div
            class="p-3 border rounded-xl border-n-weak bg-n-alpha-2"
          >
            <p
              class="text-xs font-medium text-n-slate-10"
            >
              IA desativada
            </p>

            <p
              class="mt-1 text-xl font-semibold text-n-slate-12"
            >
              {{
                Number(
                  attentionCounters.disabled || 0
                ).toLocaleString('pt-BR')
              }}
            </p>
          </div>
        </div>


<div
          v-if="dashboardLoading"
          class="p-5 mt-4 text-sm text-center text-n-slate-10"
        >
          Carregando indicadores...
        </div>


        <div
          v-else-if="
            !companyAttentionItems.length
          "
          class="p-5 mt-4 text-sm text-center border border-dashed rounded-lg border-n-teal-6 bg-n-teal-2 text-n-teal-11"
        >
          Nenhuma empresa precisa de atenção
          neste momento.
        </div>


        <div
          v-else
          class="mt-4 space-y-2"
        >
          <div
            v-for="item in companyAttentionItems"
            :key="item.account_id"

            @click="manageCompany(item)"            class="flex flex-wrap items-center justify-between gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1 cursor-pointer transition hover:border-n-brand"
          >
            <div
              class="min-w-0"
            >
              <p
                class="font-medium truncate text-n-slate-12"
              >
                {{ item.name }}
              </p>

                <div
                  class="mt-2"
                >
                  <span
                    class="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border rounded-full"
                    :class="
                      companyHealthClass(item)
                    "
                    :title="
                      companyHealth(item)
                        .description
                    "
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="
                        companyHealthDotClass(
                          item
                        )
                      "
                    />

                    Saúde:
                    {{
                      companyHealth(item).label
                    }}
                  </span>
                </div>

              <p
                class="mt-1 text-[11px] text-n-slate-9"
              >
                Clique para gerenciar
              </p>

              <div
                class="flex flex-wrap items-center gap-2 mt-2"
              >
                <span
                  v-for="reason in item.attention_reasons"
                  :key="reason.type"
                  class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full"
                  :class="{
                    'bg-n-ruby-3 text-n-ruby-11':
                      reason.type === 'whatsapp' ||
                      reason.type === 'limit',

                    'bg-n-amber-3 text-n-amber-11':
                      reason.type === 'near_limit',

                    'bg-n-slate-3 text-n-slate-11':
                      reason.type === 'disabled',
                  }"
                >
                  {{
                    reason.type === 'whatsapp'
                      ? '● '
                      : ''
                  }}{{ reason.label }}
                </span>
              </div>
            </div>


            <div
              class="flex flex-wrap items-center gap-6"
            >
              <div
                v-if="
                  item.whatsapp_provider ===
                  'evolution' &&
                  item.evolution_instance_name
                "
                class="text-right"
              >
                <p
                  class="text-xs text-n-slate-10"
                >
                  WhatsApp
                </p>

                <p
                  v-if="
                    item.evolution_state === 'open'
                  "
                  class="text-sm font-medium text-n-teal-11"
                >
                  Online
                </p>

                <p
                  v-else
                  class="text-sm font-medium text-n-ruby-11"
                >
                  Offline
                </p>
              </div>


              <div
                class="text-right"
              >
                <p
                  class="text-xs text-n-slate-10"
                >
                  Automatizados
                </p>

                <p
                  class="text-sm font-medium text-n-slate-12"
                >
                  {{
                    Number(
                      item.total_automated || 0
                    ).toLocaleString('pt-BR')
                  }}
                </p>
              </div>


              <div
                class="text-right"
              >
                <p
                  class="text-xs text-n-slate-10"
                >
                  Custo mês
                </p>

                <p
                  class="text-sm font-medium text-n-slate-12"
                >
                  {{
                    formatDashboardCurrency(
                      item.ai_cost_brl
                    )
                  }}
                </p>
              </div>


              <div
                v-if="
                  Number(
                    item.monthly_limit_brl || 0
                  ) > 0
                "
                class="w-32"
              >
                <div
                  class="flex items-center justify-between gap-2"
                >
                  <span
                    class="text-xs text-n-slate-10"
                  >
                    Limite
                  </span>

                  <span
                    class="text-xs font-medium text-n-slate-12"
                  >
                    {{
                      formatDashboardPercent(
                        item.monthly_limit_percent
                      )
                    }}%
                  </span>
                </div>

                <div
                  class="w-full h-1.5 mt-1 overflow-hidden rounded-full bg-n-alpha-3"
                >
                  <div
                    class="h-full rounded-full bg-n-brand"
                    :style="{
                      width: `${Math.min(
                        Number(
                          item.monthly_limit_percent ||
                          0
                        ),
                        100
                      )}%`,
                    }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <div class="max-w-6xl px-8 py-8 mx-auto">
      <div class="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            Empresas
          </h1>

          <p class="mt-1 text-sm text-n-slate-11">
            Gerencie as empresas, WhatsApp, IA e provisionamento da plataforma.
          </p>

          <p class="mt-2 text-sm font-medium text-n-slate-12">
            {{ companyCount }} empresa(s) cadastrada(s)
          </p>
        </div>

        <button
          type="button"
          class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
          @click="showForm = !showForm"
        >
          {{ showForm ? 'Cancelar' : '+ Nova empresa' }}
        </button>
      </div>

      <!-- DASHBOARD RESUMO EMPRESAS -->
      <div
        v-if="!loading"
        class="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 xl:grid-cols-5"
      >
        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            Empresas
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-slate-12"
          >
            {{ companyCount }}
          </div>
        </div>

        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            IAs ativas
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-teal-11"
          >
            {{ activeAiCount }}
          </div>
        </div>

        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            IAs pausadas
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-ruby-11"
          >
            {{ pausedAiCount }}
          </div>
        </div>

        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            Custo no mês
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-slate-12"
          >
            {{ formatBRL(totalMonthlyCost) }}
          </div>
        </div>

        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            Requisições IA
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-slate-12"
          >
            {{ formatNumber(totalMonthlyRequests) }}
          </div>
        </div>

        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            Respostas locais
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-teal-11"
          >
            {{ formatNumber(totalMonthlyLocalResponses) }}
          </div>
        </div>

        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            Total automatizado
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-slate-12"
          >
            {{ formatNumber(totalMonthlyAutomations) }}
          </div>
        </div>

        <div
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="text-sm text-n-slate-10">
            Sem usar IA
          </div>

          <div
            class="mt-2 text-2xl font-semibold text-n-teal-11"
          >
            {{ totalLocalPercentage.toFixed(1) }}%
          </div>
        </div>
      </div>

      <div
        v-if="error"
        class="p-3 mb-5 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{ error }}
      </div>

      <div
        v-if="success"
        class="p-3 mb-5 text-sm rounded-lg bg-n-teal-3 text-n-teal-11"
      >
        {{ success }}
      </div>

      <div
        v-if="showForm"
        class="p-6 mb-8 border rounded-xl border-n-weak bg-n-background"
      >
        <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
          Nova empresa
        </h2>

        <p class="mb-6 text-sm text-n-slate-11">
          Crie uma nova empresa e prepare a estrutura inicial da IA.
        </p>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              Nome da empresa *
            </label>

            <input
              v-model="form.name"
              type="text"
              placeholder="Ex.: Empresa ABC Ltda"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            />
          </div>

          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              Responsável
            </label>

            <input
              v-model="form.responsible"
              type="text"
              placeholder="Nome do responsável"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            />
          </div>

          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              E-mail
            </label>

            <input
              v-model="form.email"
              type="email"
              placeholder="contato@empresa.com.br"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            />
          </div>

          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">
              Telefone
            </label>

            <input
              v-model="form.phone"
              type="text"
              placeholder="(91) 99999-9999"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            />
          </div>

          
        </div>

        <div class="flex justify-end mt-6">
          <button
            type="button"
            class="px-5 py-2 text-sm font-medium text-white rounded-lg bg-n-brand disabled:opacity-50"
            :disabled="creating"
            @click="createCompany"
          >
            {{ creating ? 'Criando...' : 'Criar empresa' }}
          </button>
        </div>
      </div>

      <!-- FILTROS DASHBOARD EMPRESAS -->
      <div
        v-if="!loading && companies.length"
        class="p-5 mb-6 border rounded-xl border-n-weak bg-n-background"
      >
        <div
          class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between"
        >
          <div class="flex-1 max-w-xl">
            <label
              class="block mb-1 text-xs font-medium text-n-slate-10"
            >
              Pesquisar empresa
            </label>

            <input
              v-model="searchQuery"
              type="search"
              placeholder="Nome, responsável, e-mail, telefone ou instância..."
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            />
          </div>

          <div class="w-full xl:w-56">
            <label
              class="block mb-1 text-xs font-medium text-n-slate-10"
            >
              Ordenar por
            </label>

            <select
              v-model="sortBy"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-background text-n-slate-12"
            >
              <option value="name">
                Nome A–Z
              </option>

              <option value="name_desc">
                Nome Z–A
              </option>

              <option value="activity_desc">

                Atividade mais recente

              </option>


              <option value="activity_none">

                Sem atividade primeiro

              </option>


              <option value="cost_desc">
                Maior consumo
              </option>

              <option value="usage_desc">
                Maior utilização do limite
              </option>
            </select>
          </div>
        </div>

                <div
          v-if="companiesNeedingActionCount > 0"
          class="flex items-center justify-between gap-4 p-3 mt-5 border rounded-lg border-n-amber-6 bg-n-amber-3"
        >
          <div>
            <div
              class="text-sm font-medium text-n-amber-12"
            >
              Ações recomendadas
            </div>

            <div
              class="mt-0.5 text-xs text-n-amber-11"
            >
              {{
                companiesNeedingActionCount
              }}
              empresa(s) precisam de ação.
            </div>
          </div>

          <span
            class="inline-flex items-center justify-center min-w-8 h-8 px-2 text-sm font-semibold border rounded-full border-n-amber-6 bg-n-background text-n-amber-11"
          >
            {{
              companiesNeedingActionCount
            }}
          </span>
        </div>

<div
          class="flex flex-wrap gap-2 mt-5"
        >
          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'all'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'all'"
          >
            Todas
          </button>

          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'active'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'active'"
          >
            IA ativa
          </button>

          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'paused'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'paused'"
          >
            IA pausada
          </button>

          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'disabled'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'disabled'"
          >
            IA desativada
          </button>

          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'whatsapp_offline'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="
              statusFilter = 'whatsapp_offline'
            "
          >
            WhatsApp desconectado
          </button>

                    <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'inactive_3d'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'inactive_3d'"
          >
            Sem atividade 3+ dias
          </button>

          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'inactive_7d'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'inactive_7d'"
          >
            Sem atividade 7+ dias
          </button>

          <button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'inactive_30d'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'inactive_30d'"
          >
            Sem atividade 30+ dias
          </button>

<button
            type="button"
            class="px-3 py-2 text-sm border rounded-lg"
            :class="
              statusFilter === 'near_limit'
                ? 'border-n-brand bg-n-brand text-white'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'near_limit'"
          >
            Próximo do limite
          </button>
        </div>

        <div
          class="mt-4 text-xs text-n-slate-10"
        >
          Exibindo
          <strong class="text-n-slate-12">
            {{ filteredCompanyCount }}
          </strong>
          de
          <strong class="text-n-slate-12">
            {{ companyCount }}
          </strong>
          empresa(s).
        </div>
      </div>

      <div
        v-if="loading"
        class="py-10 text-center text-n-slate-11"
      >
        Carregando empresas...
      </div>

      <div
        v-else-if="!companies.length"
        class="p-8 text-center border rounded-xl border-n-weak text-n-slate-11"
      >
        Nenhuma empresa cadastrada.
      </div>

      <div
        v-else-if="!filteredCompanies.length"
        class="p-8 text-center border rounded-xl border-n-weak text-n-slate-11"
      >
        <div class="font-medium text-n-slate-12">
          Nenhuma empresa corresponde aos filtros.
        </div>

        <button
          type="button"
          class="mt-3 text-sm text-n-brand hover:underline"
          @click="
            searchQuery = '';
            statusFilter = 'all';
            sortBy = 'name';
          "
        >
          Limpar filtros
        </button>
      </div>

      <div
        v-else
        class="space-y-4"
      >
        <div
          v-for="company in filteredCompanies"
          :key="company.id"
          class="p-5 border rounded-xl border-n-weak bg-n-background"
        >
          <div class="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-3">
                <h2 class="text-lg font-semibold text-n-slate-12">
                  {{ company.name }}
                </h2>

                  <span
                    class="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border rounded-full"
                    :class="
                      companyHealthClass(company)
                    "
                    :title="
                      companyHealth(company)
                        .description
                    "
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :class="
                        companyHealthDotClass(
                          company
                        )
                      "
                    />

                    {{
                      companyHealth(company).label
                    }}
                  </span>

                  <span
                    class="text-xs text-n-slate-9"
                  >
                    {{
                      companyHealth(company)
                        .description
                    }}
                  </span>

                <span
                  v-if="company.disabled"
                  class="px-2 py-1 text-xs font-medium rounded bg-n-amber-3 text-n-amber-11"
                >
                  Desativada
                </span>

                <span class="text-xs text-n-slate-10">
                  Account {{ company.id }}
                </span>

                  <span
                    class="text-xs text-n-slate-9"
                  >
                    ·
                  </span>

                  <span
                    class="inline-flex flex-wrap items-center gap-1 text-xs"
                    :class="
                      company.last_activity_at
                        ? 'text-n-slate-10'
                        : 'text-n-amber-11'
                    "
                    :title="
                      lastActivityTypeLabel(company)
                    "
                  >
                    <span>
                      Última atividade:
                    </span>

                    <strong
                      class="font-medium"
                    >
                      {{
                        formatLastActivity(
                          company.last_activity_at
                        )
                      }}
                    </strong>

                    <span
                      v-if="company.last_activity_at"
                      class="text-n-slate-9"
                    >
                      ·
                      {{
                        lastActivityTypeLabel(
                          company
                        )
                      }}
                    </span>
                  </span>
                              <span
                  v-if="
                    !company.last_activity_at ||
                    Number(inactivityDays(company) || 0) >= 3
                  "
                  class="inline-flex items-center px-2 py-1 ml-2 text-xs font-medium border rounded-full"
                  :class="
                    !company.last_activity_at ||
                    Number(inactivityDays(company) || 0) >= 30
                      ? 'border-n-ruby-6 bg-n-ruby-3 text-n-ruby-11'
                      : Number(inactivityDays(company) || 0) >= 7
                        ? 'border-n-amber-6 bg-n-amber-3 text-n-amber-11'
                        : 'border-n-slate-6 bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{ inactivityLabel(company) }}
                </span>
</div>

                            <!-- ACOES RECOMENDADAS -->
              <div class="mt-4">
                <div
                  class="flex flex-wrap items-center gap-2"
                >
                  <span
                    class="text-xs font-medium text-n-slate-11"
                  >
                    Ações recomendadas:
                  </span>

                  <template
                    v-if="
                      companyRecommendedActions(
                        company
                      ).length
                    "
                  >
                    <span
                      v-for="action in companyRecommendedActions(company)"
                      :key="action.key"
                      class="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border rounded-full"
                      :class="
                        recommendedActionLevelClass(
                          action.level
                        )
                      "
                      :title="action.description"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full bg-current"
                      />

                      {{ action.title }}
                    </span>
                  </template>

                  <span
                    v-else
                    class="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium border rounded-full border-n-teal-6 bg-n-teal-3 text-n-teal-11"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full bg-current"
                    />

                    Nenhuma ação necessária
                  </span>
                </div>

                <div
                  v-if="
                    companyRecommendedActions(
                      company
                    ).length
                  "
                  class="mt-2 space-y-1"
                >
                  <div
                    v-for="action in companyRecommendedActions(company)"
                    :key="`${action.key}-description`"
                    class="text-xs text-n-slate-10"
                  >
                    <strong
                      class="font-medium text-n-slate-11"
                    >
                      {{ action.title }}:
                    </strong>

                    {{ action.description }}
                  </div>
                </div>
              </div>

<!-- DASHBOARD DETALHES EMPRESA -->
              <div
                class="grid grid-cols-1 gap-4 mt-5 md:grid-cols-2 xl:grid-cols-4"
              >
                <div
                  class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
                >
                  <div class="text-xs text-n-slate-10">
                    Inteligência Artificial
                  </div>

                  <div
                    class="flex items-center gap-2 mt-2 text-sm font-medium"
                    :class="aiStatusClass(company)"
                  >
                    <span
                      class="w-2.5 h-2.5 rounded-full shrink-0"
                      :class="aiStatusDotClass(company)"
                    />

                    {{ aiStatusLabel(company) }}
                  </div>

                  <div
                    class="mt-2 text-xs text-n-slate-10"
                  >
                    {{ modelLabel(company.ai_model) }}
                  </div>
                </div>

                <div
                  class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
                >
                  <div class="text-xs text-n-slate-10">
                    WhatsApp
                  </div>

                  <div
                    class="mt-2 text-sm font-medium"
                    :class="whatsappClass(company)"
                  >
                    {{ whatsappLabel(company) }}
                  </div>

                  <div
                    class="mt-2 text-xs text-n-slate-10"
                  >
                    {{
                      company.whatsapp_provider ===
                      'evolution'
                        ? 'Evolution API'
                        : 'API Oficial'
                    }}
                  </div>
                </div>

                <div
                  class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
                >
                  <div class="text-xs text-n-slate-10">
                    Consumo de IA no mês
                  </div>

                  <div
                    class="mt-2 text-sm font-semibold text-n-slate-12"
                  >
                    {{ formatBRL(company.monthly_cost_brl) }}
                  </div>

                  <div
                    v-if="
                      Number(
                        company.monthly_cost_limit_brl
                      ) > 0
                    "
                    class="mt-1 text-xs text-n-slate-10"
                  >
                    Limite:
                    {{
                      formatBRL(
                        company.monthly_cost_limit_brl
                      )
                    }}
                  </div>

                  <div
                    v-else
                    class="mt-1 text-xs text-n-slate-10"
                  >
                    Sem limite mensal
                  </div>
                </div>

                <div
                  class="p-4 border rounded-lg border-n-weak bg-n-alpha-1"
                >
                  <div class="text-xs text-n-slate-10">
                    Requisições no mês
                  </div>

                  <div
                    class="mt-2 text-sm font-semibold text-n-slate-12"
                  >
                    {{
                      formatNumber(
                        company.monthly_requests
                      )
                    }}
                  </div>

                  <div
                    class="mt-2 text-xs text-n-slate-10"
                  >
                    {{ company.inboxes_count }} Inbox(es)
                    ·
                    {{ company.teams_count }} Time(s)
                  </div>
                </div>
              </div>

              <div
                v-if="
                  Number(
                    company.monthly_cost_limit_brl
                  ) > 0
                "
                class="mt-5"
              >
                <div
                  class="flex items-center justify-between gap-4 mb-2 text-xs"
                >
                  <span class="text-n-slate-10">
                    Utilização do limite mensal
                  </span>

                  <span
                    class="font-medium"
                    :class="
                      company.ai_status ===
                      'paused_limit'
                        ? 'text-n-ruby-11'
                        : Number(
                            company.monthly_limit_percent ||
                              0
                          ) >= 80
                          ? 'text-n-amber-11'
                          : 'text-n-slate-11'
                    "
                  >
                    {{ usagePercentLabel(company) }}
                  </span>
                </div>

                <div
                  class="w-full h-2 overflow-hidden rounded-full bg-n-alpha-3"
                >
                  <div
                    class="h-full transition-all rounded-full"
                    :class="usageBarClass(company)"
                    :style="{
                      width: `${usagePercent(company)}%`,
                    }"
                  />
                </div>
              </div>

              <div
                v-if="company.responsible"
                class="mt-4 text-sm"
              >
                <span class="text-n-slate-10">
                  Responsável:
                </span>

                <span class="ml-1 text-n-slate-12">
                  {{ company.responsible }}
                </span>
              </div>
            </div>

            <div class="flex flex-wrap gap-2 shrink-0">
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                @click="manageCompany(company)"
              >
                Gerenciar
              </button>

              <button
                type="button"
                class="px-4 py-2 text-sm font-medium border rounded-lg border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                @click="toggleCompany(company)"
              >
                {{ company.disabled ? 'Reativar' : 'Desativar' }}
              </button>

              <button
                type="button"
                class="px-4 py-2 text-sm font-medium border rounded-lg border-n-ruby-7 text-n-ruby-11 hover:bg-n-ruby-3"
                @click="deleteCompany(company)"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
